import { useState, useEffect, useRef } from "react";
import MainLayout from "@/components/feature/MainLayout";
import { allUsers } from "@/mocks/users";
import { purchasePresets, paymentMethods } from "@/mocks/sales";

type UserType = typeof allUsers[0];

const aiTips = [
  "该用户近3个月用气量呈上升趋势，建议推荐500元套餐",
  "用户历史购气周期约45天，当前余额偏低，建议及时充值",
  "该用户已购买燃气保险，享受优先服务权益",
  "AI预测：按当前用气速率，余额将在12天后耗尽",
];

export default function SalesPurchasePage() {
  const [searchUser, setSearchUser] = useState("");
  const [selectedUser, setSelectedUser] = useState<UserType | null>(null);
  const [amount, setAmount] = useState("");
  const [payMethod, setPayMethod] = useState("wechat");
  const [showSuccess, setShowSuccess] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [aiTipIdx, setAiTipIdx] = useState(0);
  const [showAI, setShowAI] = useState(false);
  const [activeTab, setActiveTab] = useState<"realtime" | "history" | "insurance">("realtime");
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const matchedUsers = searchUser.length > 1
    ? allUsers.filter(u =>
        u.name.includes(searchUser) ||
        u.phone.includes(searchUser) ||
        u.id.includes(searchUser)
      )
    : [];

  useEffect(() => {
    const t = setInterval(() => setAiTipIdx(i => (i + 1) % aiTips.length), 4000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!selectedUser || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    const history = selectedUser.usageHistory || [];
    const last6 = history.slice(-6);
    if (last6.length === 0) return;

    const maxUsage = Math.max(...last6.map(d => d.usage));
    const barW = (w - 40) / last6.length;

    last6.forEach((d, i) => {
      const barH = ((d.usage / maxUsage) * (h - 40));
      const x = 20 + i * barW + barW * 0.15;
      const y = h - 20 - barH;
      const bw = barW * 0.7;

      const grad = ctx.createLinearGradient(x, y, x, h - 20);
      grad.addColorStop(0, "rgba(232,83,26,0.9)");
      grad.addColorStop(1, "rgba(232,83,26,0.2)");
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.roundRect(x, y, bw, barH, 3);
      ctx.fill();

      ctx.fillStyle = "rgba(100,100,100,0.7)";
      ctx.font = "9px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(d.month.slice(5) + "月", x + bw / 2, h - 5);
      ctx.fillStyle = "#E8531A";
      ctx.font = "bold 9px sans-serif";
      ctx.fillText(d.usage.toFixed(0), x + bw / 2, y - 3);
    });
  }, [selectedUser, activeTab]);

  const handlePurchase = () => {
    if (!selectedUser || !amount) return;
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3500);
  };

  const selectUser = (u: UserType) => {
    setSelectedUser(u);
    setSearchUser(u.name);
    setShowDropdown(false);
    setShowAI(true);
  };

  const gasVolume = amount ? (Number(amount) / 3.5).toFixed(2) : "0.00";
  const daysLeft = selectedUser && amount
    ? Math.floor((selectedUser.balance + Number(amount)) / (selectedUser.monthlyUsage * 3.5 / 30))
    : null;

  return (
    <MainLayout>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">购气操作台</h1>
            <p className="text-sm text-gray-500 mt-0.5">用户识别 · AI分析 · 实时购气 · 数据对比</p>
          </div>
          <div className="flex items-center gap-2 bg-orange-50 border border-orange-100 rounded-xl px-4 py-2">
            <i className="ri-robot-2-line text-[#E8531A]"></i>
            <span className="text-xs text-[#E8531A] font-medium">AI购气助手已就绪</span>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-4">
          {/* Left: User Search + Purchase */}
          <div className="col-span-5 space-y-4">
            {/* User Search */}
            <div className="bg-white rounded-xl p-5 border border-gray-100">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 flex items-center justify-center rounded-lg bg-orange-50">
                  <i className="ri-user-search-line text-[#E8531A] text-sm"></i>
                </div>
                <h3 className="text-sm font-semibold text-gray-800">用户识别查询</h3>
              </div>
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  value={searchUser}
                  onChange={e => { setSearchUser(e.target.value); setShowDropdown(true); setSelectedUser(null); setShowAI(false); }}
                  onFocus={() => setShowDropdown(true)}
                  placeholder="输入姓名、手机号、用户编号..."
                  className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#E8531A]/60"
                />
                {showDropdown && matchedUsers.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-100 rounded-xl overflow-hidden z-20">
                    {matchedUsers.map(u => (
                      <div
                        key={u.id}
                        onClick={() => selectUser(u)}
                        className="flex items-center justify-between px-4 py-3 hover:bg-orange-50 cursor-pointer border-b border-gray-50 last:border-0"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                            <span className="text-xs text-[#E8531A] font-bold">{u.name[0]}</span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-800">{u.name}</p>
                            <p className="text-xs text-gray-400">{u.phone} · {u.id} · {u.category}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-sm font-bold ${u.balance < 100 ? "text-red-600" : "text-gray-800"}`}>¥{u.balance.toFixed(2)}</p>
                          <p className="text-xs text-gray-400">余额</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {selectedUser && (
                <div className="mt-3 p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border border-orange-100">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#E8531A] flex items-center justify-center">
                        <span className="text-white font-bold text-sm">{selectedUser.name[0]}</span>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-900">{selectedUser.name}</p>
                        <p className="text-xs text-gray-500">{selectedUser.id} · {selectedUser.category}</p>
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${selectedUser.status === "正常" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                      {selectedUser.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="bg-white rounded-lg p-2 text-center">
                      <p className="text-lg font-bold text-[#E8531A]">¥{selectedUser.balance.toFixed(0)}</p>
                      <p className="text-xs text-gray-400">当前余额</p>
                    </div>
                    <div className="bg-white rounded-lg p-2 text-center">
                      <p className="text-lg font-bold text-gray-800">{selectedUser.monthlyUsage}</p>
                      <p className="text-xs text-gray-400">月均用气m³</p>
                    </div>
                    <div className="bg-white rounded-lg p-2 text-center">
                      <p className="text-lg font-bold text-green-600">{selectedUser.safetyGrade}</p>
                      <p className="text-xs text-gray-400">安全评级</p>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                    <i className="ri-map-pin-line text-gray-400"></i>
                    <span className="truncate">{selectedUser.address}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Amount */}
            <div className="bg-white rounded-xl p-5 border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">选择购气金额</h3>
              <div className="grid grid-cols-4 gap-2 mb-3">
                {purchasePresets.map(p => (
                  <button
                    key={p}
                    onClick={() => setAmount(String(p))}
                    className={`py-2.5 rounded-xl text-sm font-bold transition-colors cursor-pointer whitespace-nowrap ${amount === String(p) ? "bg-[#E8531A] text-white" : "bg-gray-100 text-gray-700 hover:bg-orange-50 hover:text-[#E8531A]"}`}
                  >
                    ¥{p}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">¥</span>
                  <input
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    placeholder="自定义金额"
                    type="number"
                    className="w-full pl-7 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#E8531A]/50"
                  />
                </div>
              </div>
              {amount && (
                <div className="mt-3 bg-orange-50 rounded-lg p-3 grid grid-cols-2 gap-2">
                  <div className="text-center">
                    <p className="text-base font-bold text-[#E8531A]">{gasVolume} m³</p>
                    <p className="text-xs text-gray-400">可购气量</p>
                  </div>
                  {daysLeft && (
                    <div className="text-center">
                      <p className="text-base font-bold text-green-600">{daysLeft} 天</p>
                      <p className="text-xs text-gray-400">预计可用天数</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Payment */}
            <div className="bg-white rounded-xl p-5 border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">支付方式</h3>
              <div className="grid grid-cols-5 gap-2">
                {paymentMethods.map(m => (
                  <button
                    key={m.id}
                    onClick={() => setPayMethod(m.id)}
                    className={`flex flex-col items-center gap-1.5 p-2.5 rounded-xl border-2 transition-colors cursor-pointer ${payMethod === m.id ? "border-[#E8531A] bg-orange-50" : "border-gray-100 hover:border-gray-200"}`}
                  >
                    <div className="w-7 h-7 flex items-center justify-center">
                      <i className={`${m.icon} text-xl`} style={{ color: m.color }}></i>
                    </div>
                    <span className="text-[10px] text-gray-600 whitespace-nowrap">{m.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={handlePurchase}
              disabled={!selectedUser || !amount}
              className="w-full py-3.5 bg-[#E8531A] text-white font-bold text-base rounded-xl hover:bg-[#d44a15] disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
            >
              确认购气 {amount ? `¥${amount}` : ""}
            </button>
          </div>

          {/* Right: Data Analysis */}
          <div className="col-span-7 space-y-4">
            {/* AI Insight */}
            {showAI && selectedUser && (
              <div className="bg-gradient-to-r from-[#0f1729] to-[#1a2540] rounded-xl p-4 border border-[#E8531A]/20">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 flex items-center justify-center rounded-lg bg-[#E8531A]/20">
                    <i className="ri-brain-line text-[#E8531A] text-sm"></i>
                  </div>
                  <span className="text-white text-sm font-semibold">AI 用户智能分析</span>
                  <span className="flex items-center gap-1 text-xs text-green-400 bg-green-400/10 px-2 py-0.5 rounded-full ml-auto">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse inline-block"></span>
                    实时分析
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div className="bg-white/5 rounded-xl p-3">
                    <p className="text-xs text-white/50 mb-1">用气行为画像</p>
                    <p className="text-xs text-white/80 leading-relaxed">
                      {"portrait" in selectedUser && selectedUser.portrait
                        ? `家庭${(selectedUser.portrait as { familySize: number }).familySize}口，${(selectedUser.portrait as { cookingFrequency: string }).cookingFrequency}，高峰时段${(selectedUser.portrait as { peakHours: string }).peakHours}`
                        : "工商业用户，用气量较大，建议大额充值"}
                    </p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-3">
                    <p className="text-xs text-white/50 mb-1">保险状态</p>
                    <div className="flex items-center gap-2">
                      <i className={selectedUser.insurance.valid ? "ri-shield-check-line text-green-400" : "ri-shield-line text-red-400"}></i>
                      <p className="text-xs text-white/80">
                        {selectedUser.insurance.valid
                          ? `${selectedUser.insurance.company} · 有效至${selectedUser.insurance.expiry}`
                          : `保险已过期（${selectedUser.insurance.expiry}）`}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-[#E8531A]/10 rounded-lg px-3 py-2 flex items-center gap-2">
                  <i className="ri-lightbulb-line text-[#E8531A] text-sm"></i>
                  <p className="text-xs text-white/80 transition-all">{aiTips[aiTipIdx]}</p>
                </div>
              </div>
            )}

            {/* Data Tabs */}
            {selectedUser ? (
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <div className="flex border-b border-gray-100">
                  {[
                    { key: "realtime", label: "实时数据", icon: "ri-pulse-line" },
                    { key: "history", label: "历史用气", icon: "ri-bar-chart-line" },
                    { key: "insurance", label: "保险信息", icon: "ri-shield-line" },
                  ].map(t => (
                    <button
                      key={t.key}
                      onClick={() => setActiveTab(t.key as typeof activeTab)}
                      className={`flex items-center gap-1.5 px-5 py-3 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap border-b-2 ${activeTab === t.key ? "border-[#E8531A] text-[#E8531A]" : "border-transparent text-gray-500 hover:text-gray-700"}`}
                    >
                      <i className={t.icon}></i>{t.label}
                    </button>
                  ))}
                </div>

                <div className="p-5">
                  {activeTab === "realtime" && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-3 gap-3">
                        {[
                          { label: "当前余额", value: `¥${selectedUser.balance.toFixed(2)}`, color: selectedUser.balance < 100 ? "text-red-600" : "text-[#E8531A]", icon: "ri-money-cny-circle-line" },
                          { label: "本月用气", value: `${selectedUser.monthlyUsage} m³`, color: "text-gray-800", icon: "ri-fire-line" },
                          { label: "年累计用气", value: `${selectedUser.yearlyUsage} m³`, color: "text-gray-800", icon: "ri-bar-chart-line" },
                          { label: "安全评级", value: selectedUser.safetyGrade, color: "text-green-600", icon: "ri-shield-check-line" },
                          { label: "上次安检", value: selectedUser.lastInspection, color: "text-gray-700", icon: "ri-calendar-check-line" },
                          { label: "下次安检", value: selectedUser.nextInspection, color: "text-amber-600", icon: "ri-calendar-line" },
                        ].map((item, i) => (
                          <div key={i} className="bg-gray-50 rounded-xl p-3">
                            <div className="flex items-center gap-1.5 mb-1">
                              <i className={`${item.icon} text-gray-400 text-xs`}></i>
                              <span className="text-xs text-gray-400">{item.label}</span>
                            </div>
                            <p className={`text-sm font-bold ${item.color}`}>{item.value}</p>
                          </div>
                        ))}
                      </div>
                      {selectedUser.devices && selectedUser.devices.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-gray-600 mb-2">关联设备</p>
                          <div className="space-y-2">
                            {selectedUser.devices.map((d, i) => (
                              <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                                <div className="flex items-center gap-2">
                                  <i className="ri-cpu-line text-gray-400 text-sm"></i>
                                  <div>
                                    <p className="text-xs font-medium text-gray-700">{d.type} · {d.model}</p>
                                    <p className="text-xs text-gray-400">{d.manufacturer} · {d.iotId}</p>
                                  </div>
                                </div>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${d.status === "正常" ? "bg-green-50 text-green-600" : "bg-amber-50 text-amber-600"}`}>{d.status}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === "history" && (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-xs font-semibold text-gray-600">近6个月用气量对比（m³）</p>
                        <div className="flex items-center gap-3 text-xs text-gray-400">
                          <span>年均：{(selectedUser.yearlyUsage / 12).toFixed(1)} m³/月</span>
                          <span>本月：{selectedUser.monthlyUsage} m³</span>
                        </div>
                      </div>
                      <canvas ref={canvasRef} width={480} height={140} className="w-full" />
                      <div className="mt-3 grid grid-cols-3 gap-2">
                        {(selectedUser.usageHistory || []).slice(-3).map((h, i) => (
                          <div key={i} className="bg-gray-50 rounded-lg p-2 text-center">
                            <p className="text-xs text-gray-400">{h.month}</p>
                            <p className="text-sm font-bold text-gray-800">{h.usage} m³</p>
                            <p className="text-xs text-[#E8531A]">¥{h.cost.toFixed(2)}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {activeTab === "insurance" && (
                    <div className="space-y-3">
                      <div className={`rounded-xl p-4 border ${selectedUser.insurance.valid ? "bg-green-50 border-green-100" : "bg-red-50 border-red-100"}`}>
                        <div className="flex items-center gap-3 mb-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${selectedUser.insurance.valid ? "bg-green-100" : "bg-red-100"}`}>
                            <i className={selectedUser.insurance.valid ? "ri-shield-check-line text-xl text-green-600" : "ri-shield-line text-xl text-red-600"}></i>
                          </div>
                          <div>
                            <p className="text-sm font-bold text-gray-900">
                              {selectedUser.insurance.valid ? "保险有效" : "保险已过期"}
                            </p>
                            <p className="text-xs text-gray-500">{selectedUser.insurance.company}</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="bg-white rounded-lg p-2">
                            <p className="text-gray-400">保险公司</p>
                            <p className="font-medium text-gray-700">{selectedUser.insurance.company}</p>
                          </div>
                          <div className="bg-white rounded-lg p-2">
                            <p className="text-gray-400">到期日期</p>
                            <p className={`font-medium ${selectedUser.insurance.valid ? "text-green-600" : "text-red-600"}`}>{selectedUser.insurance.expiry}</p>
                          </div>
                        </div>
                      </div>
                      {!selectedUser.insurance.valid && (
                        <div className="bg-amber-50 rounded-xl p-3 flex items-start gap-2">
                          <i className="ri-information-line text-amber-500 mt-0.5"></i>
                          <p className="text-xs text-amber-700">用户保险已过期，建议在购气时提醒用户续保，享受燃气安全保障服务。</p>
                        </div>
                      )}
                      <button className="w-full border border-[#E8531A] text-[#E8531A] py-2 rounded-lg text-sm cursor-pointer hover:bg-orange-50 whitespace-nowrap">
                        {selectedUser.insurance.valid ? "查看保险详情" : "推荐续保"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-100 p-12 flex flex-col items-center justify-center text-center">
                <div className="w-16 h-16 rounded-full bg-orange-50 flex items-center justify-center mb-4">
                  <i className="ri-user-search-line text-[#E8531A] text-2xl"></i>
                </div>
                <p className="text-sm font-medium text-gray-600 mb-1">请先搜索并选择用户</p>
                <p className="text-xs text-gray-400">输入姓名、手机号或用户编号进行查询</p>
              </div>
            )}

            {/* Purchase Summary */}
            {selectedUser && amount && (
              <div className="bg-white rounded-xl border border-gray-100 p-4">
                <h3 className="text-sm font-semibold text-gray-800 mb-3">本次购气预览</h3>
                <div className="grid grid-cols-4 gap-3">
                  {[
                    { label: "购气金额", value: `¥${amount}`, color: "text-[#E8531A]" },
                    { label: "可购气量", value: `${gasVolume} m³`, color: "text-gray-800" },
                    { label: "充值后余额", value: `¥${(selectedUser.balance + Number(amount)).toFixed(2)}`, color: "text-green-600" },
                    { label: "预计可用", value: `${daysLeft || "-"} 天`, color: "text-gray-700" },
                  ].map((item, i) => (
                    <div key={i} className="bg-gray-50 rounded-xl p-3 text-center">
                      <p className={`text-base font-bold ${item.color}`}>{item.value}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{item.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showSuccess && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/30">
          <div className="bg-white rounded-2xl p-8 text-center w-80">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-check-line text-green-600 text-3xl"></i>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">购气成功！</h3>
            <p className="text-sm text-gray-500 mb-1">已向IoT系统下发充值指令</p>
            <p className="text-xs text-gray-400">¥{amount} · {gasVolume} m³ · {selectedUser?.name}</p>
            <div className="mt-4 flex gap-2">
              <button onClick={() => setShowSuccess(false)} className="flex-1 border border-gray-200 text-gray-600 py-2 rounded-lg text-sm cursor-pointer whitespace-nowrap">关闭</button>
              <button onClick={() => setShowSuccess(false)} className="flex-1 bg-[#E8531A] text-white py-2 rounded-lg text-sm cursor-pointer whitespace-nowrap">打印小票</button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
}
